import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

interface Character {
  character: string;
  pinyin: string;
  meaning: string;
  usage: string;
  elements: string[];
}

export function NameMeanings() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [
    "Virtues",
    "Nature",
    "Aspirations",
    "Qualities",
    "Elements",
    "All"
  ];

  const characters: Character[] = [
    // Virtues
    { character: "德", pinyin: "Dé", meaning: "Virtue, morality", usage: "Often used in names to represent moral character and virtue", elements: ["Virtues"] },
    { character: "智", pinyin: "Zhì", meaning: "Wisdom, intelligence", usage: "Popular in names for expressing hopes for intellectual achievement", elements: ["Virtues", "Qualities"] },
    { character: "仁", pinyin: "Rén", meaning: "Benevolence, kindness", usage: "Represents compassion and humaneness in Confucian philosophy", elements: ["Virtues"] },
    { character: "义", pinyin: "Yì", meaning: "Righteousness, justice", usage: "Symbolizes moral integrity and doing what is right", elements: ["Virtues"] },
    { character: "礼", pinyin: "Lǐ", meaning: "Ritual, propriety", usage: "Represents respect for tradition and proper conduct", elements: ["Virtues"] },
    { character: "信", pinyin: "Xìn", meaning: "Trust, faith", usage: "Indicates reliability and trustworthiness", elements: ["Virtues", "Qualities"] },
    { character: "忠", pinyin: "Zhōng", meaning: "Loyalty, devotion", usage: "Represents faithfulness and dedication", elements: ["Virtues"] },
    { character: "孝", pinyin: "Xiào", meaning: "Filial piety", usage: "Honors respect for parents and ancestors", elements: ["Virtues"] },
    
    // Nature
    { character: "山", pinyin: "Shān", meaning: "Mountain", usage: "Represents strength and stability in names", elements: ["Nature", "Elements"] },
    { character: "海", pinyin: "Hǎi", meaning: "Ocean, sea", usage: "Symbolizes vastness and depth of character", elements: ["Nature", "Elements"] },
    { character: "水", pinyin: "Shuǐ", meaning: "Water", usage: "Represents flexibility and adaptability", elements: ["Nature", "Elements"] },
    { character: "火", pinyin: "Huǒ", meaning: "Fire", usage: "Symbolizes passion and energy", elements: ["Nature", "Elements"] },
    { character: "木", pinyin: "Mù", meaning: "Wood, tree", usage: "Represents growth and vitality", elements: ["Nature", "Elements"] },
    { character: "金", pinyin: "Jīn", meaning: "Gold, metal", usage: "Symbolizes value and refinement", elements: ["Nature", "Elements"] },
    { character: "土", pinyin: "Tǔ", meaning: "Earth, soil", usage: "Represents stability and foundation", elements: ["Nature", "Elements"] },
    { character: "风", pinyin: "Fēng", meaning: "Wind", usage: "Symbolizes freedom and movement", elements: ["Nature"] },
    { character: "云", pinyin: "Yún", meaning: "Cloud", usage: "Represents elegance and transcendence", elements: ["Nature"] },
    { character: "月", pinyin: "Yuè", meaning: "Moon", usage: "Symbolizes beauty and tranquility", elements: ["Nature"] },
    { character: "星", pinyin: "Xīng", meaning: "Star", usage: "Represents brightness and guidance", elements: ["Nature", "Aspirations"] },
    { character: "花", pinyin: "Huā", meaning: "Flower", usage: "Symbolizes beauty and grace", elements: ["Nature"] },
    { character: "梅", pinyin: "Méi", meaning: "Plum blossom", usage: "Represents resilience and perseverance", elements: ["Nature"] },
    { character: "竹", pinyin: "Zhú", meaning: "Bamboo", usage: "Symbolizes flexibility and strength", elements: ["Nature"] },
    { character: "松", pinyin: "Sōng", meaning: "Pine tree", usage: "Represents longevity and steadfastness", elements: ["Nature"] },
    
    // Aspirations
    { character: "永", pinyin: "Yǒng", meaning: "Forever, eternal", usage: "Used to express wishes for longevity and endurance", elements: ["Aspirations"] },
    { character: "远", pinyin: "Yuǎn", meaning: "Far, distant", usage: "Represents ambition and far-reaching goals", elements: ["Aspirations"] },
    { character: "高", pinyin: "Gāo", meaning: "High, tall", usage: "Symbolizes lofty aspirations and excellence", elements: ["Aspirations", "Qualities"] },
    { character: "强", pinyin: "Qiáng", meaning: "Strong, powerful", usage: "Represents strength and determination", elements: ["Aspirations", "Qualities"] },
    { character: "伟", pinyin: "Wěi", meaning: "Great, magnificent", usage: "Indicates greatness and achievement", elements: ["Aspirations"] },
    { character: "成", pinyin: "Chéng", meaning: "Success, achievement", usage: "Represents accomplishment and fulfillment", elements: ["Aspirations"] },
    { character: "达", pinyin: "Dá", meaning: "Reach, achieve", usage: "Symbolizes reaching goals and success", elements: ["Aspirations"] },
    { character: "兴", pinyin: "Xīng", meaning: "Prosper, flourish", usage: "Represents prosperity and flourishing", elements: ["Aspirations"] },
    { character: "富", pinyin: "Fù", meaning: "Rich, wealthy", usage: "Symbolizes abundance and prosperity", elements: ["Aspirations"] },
    { character: "贵", pinyin: "Guì", meaning: "Noble, precious", usage: "Represents nobility and value", elements: ["Aspirations", "Qualities"] },
    
    // Qualities
    { character: "明", pinyin: "Míng", meaning: "Bright, clear", usage: "Common in names to represent clarity of mind and brightness", elements: ["Qualities", "Aspirations"] },
    { character: "亮", pinyin: "Liàng", meaning: "Bright, shining", usage: "Represents brilliance and clarity", elements: ["Qualities"] },
    { character: "美", pinyin: "Měi", meaning: "Beautiful, fine", usage: "Symbolizes beauty and excellence", elements: ["Qualities"] },
    { character: "好", pinyin: "Hǎo", meaning: "Good, excellent", usage: "Represents goodness and quality", elements: ["Qualities"] },
    { character: "善", pinyin: "Shàn", meaning: "Good, kind", usage: "Indicates goodness and kindness", elements: ["Qualities", "Virtues"] },
    { character: "勇", pinyin: "Yǒng", meaning: "Brave, courageous", usage: "Represents courage and bravery", elements: ["Qualities"] },
    { character: "刚", pinyin: "Gāng", meaning: "Strong, firm", usage: "Symbolizes strength and firmness", elements: ["Qualities"] },
    { character: "柔", pinyin: "Róu", meaning: "Soft, gentle", usage: "Represents gentleness and flexibility", elements: ["Qualities"] },
    { character: "静", pinyin: "Jìng", meaning: "Quiet, calm", usage: "Symbolizes tranquility and peace", elements: ["Qualities"] },
    { character: "雅", pinyin: "Yǎ", meaning: "Elegant, refined", usage: "Represents elegance and refinement", elements: ["Qualities"] },
    { character: "文", pinyin: "Wén", meaning: "Literature, culture", usage: "Symbolizes learning and culture", elements: ["Qualities", "Aspirations"] },
    { character: "武", pinyin: "Wǔ", meaning: "Martial, military", usage: "Represents martial prowess and strength", elements: ["Qualities"] },
    { character: "博", pinyin: "Bó", meaning: "Extensive, learned", usage: "Indicates broad knowledge and learning", elements: ["Qualities", "Aspirations"] },
    { character: "学", pinyin: "Xué", meaning: "Study, learn", usage: "Represents love of learning", elements: ["Qualities", "Aspirations"] },
    { character: "才", pinyin: "Cái", meaning: "Talent, ability", usage: "Symbolizes natural talent and ability", elements: ["Qualities"] },
    { character: "艺", pinyin: "Yì", meaning: "Art, skill", usage: "Represents artistic talent and skill", elements: ["Qualities"] },
    { character: "敏", pinyin: "Mǐn", meaning: "Quick, agile", usage: "Indicates quickness and agility", elements: ["Qualities"] },
    { character: "慧", pinyin: "Huì", meaning: "Wisdom, intelligence", usage: "Represents wisdom and intelligence", elements: ["Qualities", "Virtues"] },
    { character: "聪", pinyin: "Cōng", meaning: "Clever, intelligent", usage: "Symbolizes cleverness and intelligence", elements: ["Qualities"] },
    { character: "灵", pinyin: "Líng", meaning: "Spirit, clever", usage: "Represents spiritual awareness and cleverness", elements: ["Qualities"] },
    { character: "健", pinyin: "Jiàn", meaning: "Healthy, strong", usage: "Symbolizes health and strength", elements: ["Qualities"] },
    { character: "康", pinyin: "Kāng", meaning: "Healthy, peaceful", usage: "Represents health and peace", elements: ["Qualities", "Aspirations"] },
    { character: "安", pinyin: "Ān", meaning: "Peace, safe", usage: "Indicates peace and safety", elements: ["Qualities", "Aspirations"] },
    { character: "乐", pinyin: "Lè", meaning: "Happy, joyful", usage: "Represents happiness and joy", elements: ["Qualities", "Aspirations"] },
    { character: "欢", pinyin: "Huān", meaning: "Happy, joyful", usage: "Symbolizes joy and happiness", elements: ["Qualities"] },
    { character: "喜", pinyin: "Xǐ", meaning: "Happy, like", usage: "Represents happiness and joy", elements: ["Qualities"] },
    { character: "欣", pinyin: "Xīn", meaning: "Happy, joyful", usage: "Indicates joy and happiness", elements: ["Qualities"] },
    { character: "怡", pinyin: "Yí", meaning: "Happy, pleasant", usage: "Symbolizes pleasantness and joy", elements: ["Qualities"] },
    { character: "婷", pinyin: "Tíng", meaning: "Graceful, elegant", usage: "Represents grace and elegance", elements: ["Qualities"] },
    { character: "婉", pinyin: "Wǎn", meaning: "Gentle, graceful", usage: "Indicates gentleness and grace", elements: ["Qualities"] },
    { character: "淑", pinyin: "Shū", meaning: "Kind, virtuous", usage: "Represents kindness and virtue", elements: ["Qualities", "Virtues"] },
    { character: "贤", pinyin: "Xián", meaning: "Worthy, virtuous", usage: "Symbolizes worthiness and virtue", elements: ["Qualities", "Virtues"] },
    { character: "杰", pinyin: "Jié", meaning: "Outstanding, hero", usage: "Represents excellence and heroism", elements: ["Qualities", "Aspirations"] },
    { character: "豪", pinyin: "Háo", meaning: "Heroic, bold", usage: "Indicates heroism and boldness", elements: ["Qualities"] },
    { character: "雄", pinyin: "Xióng", meaning: "Heroic, powerful", usage: "Symbolizes heroism and power", elements: ["Qualities"] },
    { character: "英", pinyin: "Yīng", meaning: "Hero, flower", usage: "Represents heroism and excellence", elements: ["Qualities", "Nature"] },
    { character: "华", pinyin: "Huá", meaning: "Magnificent, China", usage: "Indicates magnificence and Chinese identity", elements: ["Qualities", "Aspirations"] },
    { character: "荣", pinyin: "Róng", meaning: "Glory, honor", usage: "Represents glory and honor", elements: ["Qualities", "Aspirations"] },
    { character: "耀", pinyin: "Yào", meaning: "Shine, brilliant", usage: "Symbolizes brilliance and radiance", elements: ["Qualities"] },
    { character: "光", pinyin: "Guāng", meaning: "Light, bright", usage: "Represents brightness and light", elements: ["Qualities"] },
    { character: "辉", pinyin: "Huī", meaning: "Bright, glorious", usage: "Indicates brightness and glory", elements: ["Qualities"] },
    { character: "晨", pinyin: "Chén", meaning: "Morning, dawn", usage: "Symbolizes new beginnings and hope", elements: ["Qualities", "Nature"] },
    { character: "曦", pinyin: "Xī", meaning: "Sunlight, dawn", usage: "Represents dawn and new beginnings", elements: ["Qualities", "Nature"] },
    { character: "阳", pinyin: "Yáng", meaning: "Sun, positive", usage: "Indicates positivity and sunshine", elements: ["Qualities", "Nature"] },
    { character: "宇", pinyin: "Yǔ", meaning: "Universe, space", usage: "Represents vastness and universe", elements: ["Qualities", "Nature"] },
    { character: "天", pinyin: "Tiān", meaning: "Sky, heaven", usage: "Symbolizes heaven and sky", elements: ["Qualities", "Nature"] },
    { character: "地", pinyin: "Dì", meaning: "Earth, ground", usage: "Represents earth and foundation", elements: ["Qualities", "Nature"] },
    { character: "和", pinyin: "Hé", meaning: "Harmony, peace", usage: "Indicates harmony and peace", elements: ["Qualities", "Virtues"] },
    { character: "平", pinyin: "Píng", meaning: "Peace, level", usage: "Represents peace and equality", elements: ["Qualities", "Aspirations"] },
    { character: "顺", pinyin: "Shùn", meaning: "Smooth, favorable", usage: "Symbolizes smoothness and favor", elements: ["Qualities", "Aspirations"] },
    { character: "利", pinyin: "Lì", meaning: "Benefit, profit", usage: "Represents benefit and advantage", elements: ["Qualities", "Aspirations"] },
    { character: "福", pinyin: "Fú", meaning: "Blessing, fortune", usage: "Indicates blessings and good fortune", elements: ["Qualities", "Aspirations"] },
    { character: "寿", pinyin: "Shòu", meaning: "Longevity, age", usage: "Represents long life and longevity", elements: ["Qualities", "Aspirations"] },
    { character: "长", pinyin: "Cháng", meaning: "Long, lasting", usage: "Symbolizes longevity and endurance", elements: ["Qualities", "Aspirations"] },
    { character: "久", pinyin: "Jiǔ", meaning: "Long time, lasting", usage: "Represents lasting and endurance", elements: ["Qualities", "Aspirations"] }
  ];

  const filteredCharacters = characters.filter(char => {
    const matchesSearch = 
      char.character.includes(searchTerm) ||
      char.pinyin.toLowerCase().includes(searchTerm.toLowerCase()) ||
      char.meaning.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = 
      !selectedCategory || 
      selectedCategory === "All" || 
      char.elements.includes(selectedCategory);

    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <SeoMeta
        title="Chinese Name Character Meanings - Dictionary & Guide"
        description="Want to understand what Chinese characters mean in names? Explore our comprehensive dictionary of Chinese name characters with meanings, pinyin pronunciation, and usage examples. Learn how characters like 德 (virtue), 智 (wisdom), and 明 (bright) create meaningful names. Perfect for choosing characters that match your values and aspirations."
        keywords="chinese name character meanings, chinese character dictionary, chinese name meanings, chinese characters in names, chinese name character guide, chinese name meaning lookup, chinese character meanings for names"
        pathname="/name-meanings"
      />
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-4">Chinese Name Character Meanings</h1>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Explore the meanings and usage of Chinese characters in names. Each character carries deep cultural significance and contributes to the overall meaning of a Chinese name.
            </p>
          </div>

          {/* Introduction Section */}
          <section className="mb-12 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding Chinese Name Characters</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Chinese names are composed of characters (汉字), each with its own meaning, pronunciation, and cultural significance. Unlike English names, Chinese names are carefully chosen to reflect parents' hopes, values, and aspirations for their children.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Characters are typically selected based on:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li><strong>Meaning:</strong> Each character carries specific meanings related to virtues, nature, qualities, or aspirations</li>
              <li><strong>Sound:</strong> Characters are chosen for their pleasant pronunciation and tone harmony</li>
              <li><strong>Cultural significance:</strong> Many characters have historical or cultural associations</li>
              <li><strong>Five elements:</strong> Traditional Chinese naming considers the balance of five elements (五行)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Use this dictionary to explore over 80 common Chinese characters used in names, their meanings, and how they're typically used in name combinations.
            </p>
          </section>

          {/* How to Use Section */}
          <section className="mb-12 bg-white border-l-4 border-indigo-500 p-6 rounded">
            <h2 className="text-xl font-bold text-gray-900 mb-4">How to Use This Dictionary</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Search by Character</h3>
                <p className="text-gray-600 text-sm">
                  Type a Chinese character directly into the search box to find its meaning and usage.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Search by Pinyin</h3>
                <p className="text-gray-600 text-sm">
                  Enter the pinyin pronunciation (e.g., "ming", "de") to find characters by sound.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Filter by Category</h3>
                <p className="text-gray-600 text-sm">
                  Use category buttons to filter characters by Virtues, Nature, Aspirations, Qualities, or Elements.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Understand Usage</h3>
                <p className="text-gray-600 text-sm">
                  Each character entry includes its meaning, typical usage in names, and related categories.
                </p>
              </div>
            </div>
          </section>

        {/* Search and Filter Section */}
        <div className="mt-8 max-w-xl mx-auto">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search characters, pinyin, or meanings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category === selectedCategory ? null : category)}
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  category === selectedCategory
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Showing <strong className="text-indigo-600">{filteredCharacters.length}</strong> of <strong className="text-indigo-600">{characters.length}</strong> characters
          </p>
        </div>

        {/* Characters Grid */}
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCharacters.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 text-lg">No characters found matching your search.</p>
              <p className="text-gray-400 text-sm mt-2">Try adjusting your search terms or category filter.</p>
            </div>
          ) : (
            filteredCharacters.map((char) => (
            <div
              key={char.character}
              className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center flex-1">
                    <h3 className="text-5xl font-bold text-gray-900 mb-2">{char.character}</h3>
                    <p className="text-xl text-indigo-600">{char.pinyin}</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Meaning</h4>
                  <p className="mt-1 text-gray-900">{char.meaning}</p>
                </div>
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-500">Usage in Names</h4>
                  <p className="mt-1 text-gray-600">{char.usage}</p>
                </div>
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-500">Categories</h4>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {char.elements.map((element) => (
                      <span
                        key={element}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                      >
                        {element}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))
          )}
        </div>

        {/* Additional Resources Section */}
        <section className="mt-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Learn More About Chinese Names</h2>
          <p className="text-gray-600 mb-6">
            Understanding character meanings is just the beginning. Explore our other resources to learn more about Chinese naming traditions.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <a href="/name-generator" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-gray-900 mb-2">Name Generator</h3>
              <p className="text-sm text-gray-600">Generate personalized Chinese names with meanings</p>
            </a>
            <a href="/pronunciation" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-gray-900 mb-2">Pronunciation Guide</h3>
              <p className="text-sm text-gray-600">Learn how to pronounce Chinese names correctly</p>
            </a>
            <a href="/chinese-boy-names" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-gray-900 mb-2">Boy Names</h3>
              <p className="text-sm text-gray-600">Explore Chinese boy names with meanings</p>
            </a>
          </div>
        </section>
      </div>
    </div>
    </>
  );
}